import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useSession } from '@/features/auth/session'
import { translations, type Lang } from './translations'
import { isLocalizedZone, pathInLang } from './lang-path'
import { screenLang, urlLang } from './lang-choice'
import { LanguageContext, type LanguageContextValue } from './context'

const STORAGE_KEY = 'achp-lang'

/**
 * Язык определяет АДРЕС — на витрине; вне её — выбор человека.
 *
 * Главный язык — английский, он живёт в корне; русский — под `/ru`. Так у
 * каждой страницы витрины ровно один адрес на язык: его можно отправить, он
 * попадает в выдачу и связывается через hreflang. Прежняя схема — «тот же URL,
 * но иногда по-русски» — не объяснима ни поисковику, ни собеседнику, которому
 * прислали ссылку.
 *
 * Сохранённый выбор живёт для зон, у которых своего адреса на язык нет:
 * кабинет, моделлер, экраны входа. Правило целиком — в `lang-choice.ts`.
 */
function readChosen(): Lang | null {
  if (typeof window === 'undefined') return null
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'en' || saved === 'ru' ? saved : null
  } catch {
    /* private mode */
    return null
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname })
  // Прочитано до первой отрисовки — раньше сайт рисовался по-английски и
  // переворачивался в русский на монтировании, и это было видно на каждой
  // загрузке. Читается НЕЗАВИСИМО от адреса загрузки: вход через английский
  // корень не должен стирать выбор, сделанный для кабинета.
  const [chosen, setChosen] = useState<Lang | null>(readChosen)
  // Язык последней витрины в этой вкладке — запасной для зон без адреса,
  // когда выбора нет. Обновляется при рендере, а не эффектом: иначе первый
  // кадр кабинета после русской витрины успевал бы нарисоваться по-английски.
  const [lastStorefront, setLastStorefront] = useState<Lang>(() => urlLang(path))
  const storefront = isLocalizedZone(path) ? urlLang(path) : lastStorefront
  if (storefront !== lastStorefront) setLastStorefront(storefront)
  const lang = screenLang(path, chosen, storefront)

  const navigate = useNavigate()

  /**
   * Язык и пояс — ещё и серверу: письма он пишет на языке человека и называет
   * время в его поясе, а знает их только браузер. Язык аккаунта — выбор
   * переключателем, без выбора — язык, на котором заговорил бы кабинет
   * (последней витрины); такой язык только заполняет пустое место и не
   * перетирает сказанное при регистрации (`localePatch`). Уходит при входе —
   * загрузке приложения вошедшим — и при каждом переключении, и только если
   * отличается от того, что сервер уже знает. Сам запрос, его тишина и
   * очередь — в `session.ts`; экраны об этом не знают.
   */
  const account = useRef({ lang: chosen ?? storefront, chosen: chosen !== null })
  useEffect(() => {
    account.current = { lang: chosen ?? storefront, chosen: chosen !== null }
  })
  const signedIn = useSession((s) => (s.status === 'authenticated' ? s.user?.id : undefined))
  useEffect(() => {
    if (signedIn === undefined) return
    void useSession.getState().syncLocale(account.current.lang, account.current.chosen)
  }, [signedIn])

  /**
   * На витрине смена языка — это ПЕРЕХОД по адресу, а не изменение состояния:
   * иначе русская страница осталась бы жить по английскому адресу, и ссылка,
   * отправленная другу, открылась бы у него по-английски.
   */
  const setLang = useCallback(
    (next: Lang) => {
      setChosen(next)
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* private mode */
      }
      void useSession.getState().syncLocale(next, true)
      if (isLocalizedZone(path)) {
        void navigate({ to: pathInLang(path, next) as never, search: (prev: unknown) => prev as never })
      }
    },
    [navigate, path],
  )

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      t: translations[lang],
      setLang,
      toggleLang: () => setLang(lang === 'en' ? 'ru' : 'en'),
    }),
    [lang, setLang],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
